import React, { useEffect, useRef, useState, FunctionComponent, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useFetch } from '@api/ApiHelper';
import { CustomFieldValue } from '@api/v4/assets/customFieldTypes';
import { sortByPosition } from '@api/v4/assets/helpers';
import { CustomFieldKeysListResponse, CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import { valuesAsString } from '@components/asset/modal/tabs/asset_details/helpers/custom-field-strings';
import { CustomFieldKeyValuesEntry, FlattenedCustomFieldKeyValuesMap } from '@components/asset/modal/tabs/edit/EditTabTypes';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { CustomFieldKeyValueRow } from '@components/asset/modal/tabs/edit/main_pane/custom_fields/custom-field-row/CustomFieldKeyValueRow';
import { TextButton } from '@components/library/button';
import { isMissingCustomFieldValue } from '@components/show_page/sections/asset/required-custom-fields-dialog-helper';
import { getDependentCFKeys, matchingCustomFieldsForDCFs, removeDuplicateFields, toggleDependentFields } from '@helpers/dependent_custom_fields';

export interface GuestUploadCustomFieldsProps {
  brandfolderKey: string;
  newAssetKeys: string[];
  platformName: string;
  controlledCustomFields: boolean;
  resetForm: boolean;
  setAllRequiredCustomFields: SetStateDispatch<boolean>;
  setFinalCustomFields: SetStateDispatch<FlattenedCustomFieldKeyValuesMap>;
  setSubmissionAttempted: SetStateDispatch<boolean>;
  submissionAttempted: boolean;
  uploadLinkKey: string;
}

const createEmptyCustomFieldMap = (
  customFieldKeys: CustomFieldKeysResponseObject[]
): FlattenedCustomFieldKeyValuesMap => {
  const map: FlattenedCustomFieldKeyValuesMap = {};
  customFieldKeys.forEach((customFieldKey) => {
    map[customFieldKey.id] = {
      customFieldKey: {
        id: customFieldKey.id,
        name: customFieldKey.attributes.name
      },
      customFieldValues: [],
      customFieldTouched: false
    };
  });
  return map;
};

export const GuestUploadCustomFields: FunctionComponent<GuestUploadCustomFieldsProps> = ({
  brandfolderKey,
  newAssetKeys,
  platformName,
  controlledCustomFields,
  resetForm,
  setAllRequiredCustomFields,
  setFinalCustomFields,
  setSubmissionAttempted,
  submissionAttempted,
  uploadLinkKey
}) => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [requiredCustomFieldKeys, setRequiredCustomFieldKeys] = useState<CustomFieldKeysResponseObject[]>([]);
  const [customFieldsMap, setCustomFieldsMap] = useState<FlattenedCustomFieldKeyValuesMap>(createEmptyCustomFieldMap(requiredCustomFieldKeys));
  const requiredCustomFieldsFetch = useFetch<CustomFieldKeysListResponse>({
    url: `/api/v4/brandfolders/${brandfolderKey}/custom_field_keys`,
    fetchOnMount: false,
    fields: 'multi_value_enabled',
    params: {
      include: 'dependent_custom_fields',
      order: 'ASC',
      per: 3000,
      sort_by: 'name', // eslint-disable-line @typescript-eslint/naming-convention
      ugt_locale: determineUGTLocale(), // eslint-disable-line @typescript-eslint/naming-convention
      upload_link_key: uploadLinkKey // eslint-disable-line @typescript-eslint/naming-convention
    },
  });
  const tempValuePrefix = 'create-cfv';
  const listContainerRef = useRef(null);
  const dcfResponse = requiredCustomFieldsFetch.response?.included;

  useEffect(() => {
    if (resetForm) {
      setCustomFieldsMap(createEmptyCustomFieldMap(requiredCustomFieldKeys));
    }
  }, [resetForm]);

  useEffect(() => {
    if (controlledCustomFields) {
      requiredCustomFieldsFetch.fetch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (requiredCustomFieldsFetch.response) {
      const dependentCFKeys = getDependentCFKeys(dcfResponse);
      const requiredCFKeys = requiredCustomFieldsFetch.response.data.filter((customFieldKey) =>
        !!customFieldKey.attributes.required &&
        // initially only show fields that aren't dependent fields
        !dependentCFKeys?.includes(customFieldKey.id)
      );

      if (requiredCFKeys.length === 0) {
        setAllRequiredCustomFields(true);
      }

      setRequiredCustomFieldKeys(requiredCFKeys);
      setCustomFieldsMap(createEmptyCustomFieldMap(requiredCFKeys));
      setContainerHeight(listContainerRef.current?.clientHeight);
    }
  }, [requiredCustomFieldsFetch.response]);

  const updateCustomFieldsMap = (
    customFieldKeyId: string,
    customFieldValue: string
  ): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    const selectedCustomField = requiredCustomFieldsFetch.response.data?.find(
      ({ id }) => id === customFieldKeyId
    );
    // if a default wasn't already created, add the new cf + values to the object
    if (!customFieldsMapCopy[customFieldKeyId]) {
      Object.assign(customFieldsMapCopy, {
        [customFieldKeyId]: {
          customFieldKey: {
            id: customFieldKeyId,
            name: selectedCustomField.attributes.name,
          },
          customFieldValues: [
            {
              key: selectedCustomField.id,
              value: customFieldValue || ''
            },
          ],
          customFieldTouched: true
        },
      });
    } else
      // if the id already exists in the object, just update the values to reflect the selection
      customFieldsMapCopy[customFieldKeyId] = {
        ...customFieldsMap[customFieldKeyId],
        customFieldValues: [
          ...customFieldsMap[customFieldKeyId]?.customFieldValues,
          {
            key: `${tempValuePrefix}-${uuidv4()}`,
            value: customFieldValue || ''
          },
        ],
        customFieldTouched: true
      };

    setCustomFieldsMap(customFieldsMapCopy);
  };

  const updateRequiredKeysWithDCFs = (customFieldValue: string): void => {
    if (dcfResponse?.length) {
      const relevantDependentCFs = dcfResponse.filter(({ attributes }) => attributes.value === customFieldValue);
      const newCustomFieldKeys = matchingCustomFieldsForDCFs(
        requiredCustomFieldsFetch.response.data,
        relevantDependentCFs
      );

      setRequiredCustomFieldKeys([
        ...requiredCustomFieldKeys,
        ...newCustomFieldKeys,
      ]);
    }
  };

  const handleCreate = (customFieldKeyId: string, customFieldValue: CustomFieldValue): void => {
    updateCustomFieldsMap(customFieldKeyId, customFieldValue.value);
    updateRequiredKeysWithDCFs(customFieldValue.value);
  };

  const handleUpdateDependentFields = (
    customFieldKeyId: string,
    customFieldValue: CustomFieldValue,
    customFieldsMapCopy: { [x: string]: CustomFieldKeyValuesEntry }
  ): void => {
    const dependentFields = toggleDependentFields(dcfResponse, customFieldValue.value, customFieldKeyId);
    const newCustomFieldKeys = matchingCustomFieldsForDCFs(requiredCustomFieldsFetch.response?.data, dependentFields?.keep);
    const allCustomFieldKeys = [
      ...requiredCustomFieldKeys,
      ...newCustomFieldKeys,
    ];
    const fieldsToDelete = dependentFields?.remove;
    const fieldsToDeleteIds = fieldsToDelete?.map(({ attributes }) => attributes.child_key);
    const filteredRequiredKeys = removeDuplicateFields(allCustomFieldKeys).filter(
      ({ id }) => !getDependentCFKeys(dependentFields?.remove)?.includes(id)
    );

    fieldsToDeleteIds?.forEach((id) => delete customFieldsMapCopy[id]);

    setCustomFieldsMap(customFieldsMapCopy);
    setRequiredCustomFieldKeys(filteredRequiredKeys);
  };

  const genHandleDelete = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    const valuesCopy: CustomFieldValue[] = [];
    customFieldsMap[customFieldKeyId].customFieldValues.forEach((cfv) => {
      if (cfv.key !== customFieldValue.key) {
        valuesCopy.push(cfv);
      }
    });
    customFieldsMapCopy[customFieldKeyId] = {
      ...customFieldsMap[customFieldKeyId],
      customFieldValues: valuesCopy,
      customFieldTouched: true
    };
    setCustomFieldsMap(customFieldsMapCopy);
  };

  const genHandleCreate = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    handleCreate(customFieldKeyId, customFieldValue);
  };

  const genHandleUpdate = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    if (!customFieldValue.key) {
      return handleCreate(customFieldKeyId, customFieldValue);
    }
    if (dcfResponse?.length) {
      handleUpdateDependentFields(customFieldKeyId, customFieldValue, customFieldsMapCopy);
    }
    const valuesCopy = customFieldsMapCopy[customFieldKeyId].customFieldValues.map((cfv) => {
      if (cfv.key === customFieldValue.key) {
        return customFieldValue;
      }

      return cfv;
    });

    // clear out both value AND the temp key if the value is empty
    const resetSingleEntryValues = valuesCopy.length === 1 && !valuesCopy[0].value;
    customFieldsMapCopy[customFieldKeyId] = {
      ...customFieldsMap[customFieldKeyId],
      customFieldValues: resetSingleEntryValues ? [] : valuesCopy,
      customFieldTouched: true
    };
    return setCustomFieldsMap(customFieldsMapCopy);
  };

  const handleRemoveValues = (cfk: string): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    // set selected field to empty values
    customFieldsMapCopy[cfk] = {
      ...customFieldsMap[cfk],
      customFieldValues: []
    };

    if (dcfResponse?.length) {
      const dependentFieldsToRemove = dcfResponse.filter(({ attributes }) => attributes.parent_key === cfk);
      const dcfIds = getDependentCFKeys(dependentFieldsToRemove);
      const updatedRequiredFields = requiredCustomFieldKeys.filter(({ id }) => !dcfIds.includes(id));

      dcfIds.forEach((id) => delete customFieldsMapCopy[id]);

      setRequiredCustomFieldKeys(updatedRequiredFields);
    }

    setCustomFieldsMap(customFieldsMapCopy);
  };

  const handleCustomFieldsSubmission = (): void => {
    let missingRequiredField = false;
    const customFieldsMapCopy: FlattenedCustomFieldKeyValuesMap = {};
    requiredCustomFieldKeys.forEach((requiredKey) => {
      customFieldsMapCopy[requiredKey.id] = {
        ...customFieldsMap[requiredKey.id],
        customFieldTouched: true
      };
      if (!missingRequiredField) {
        missingRequiredField = isMissingCustomFieldValue(customFieldsMap[requiredKey.id]);
      }
    });

    if (missingRequiredField) {
      setCustomFieldsMap(customFieldsMapCopy);
      setSubmissionAttempted(false);
      setAllRequiredCustomFields(false);
    } else {
      setFinalCustomFields(customFieldsMap);
      setAllRequiredCustomFields(true);
    }
  };

  useEffect(() => {
    if (submissionAttempted) {
      handleCustomFieldsSubmission();
    }
  }, [submissionAttempted]);

  const renderCustomFields = ({ customFieldKey, customFieldValues }): ReactNode => (
    <tr
      key={customFieldKey.id}
      className="guest-upload__required-custom-fields--table-row"
    >
      <td className="guest-upload__required-custom-fields--table-name">{customFieldKey.name}</td>
      <td className="guest-upload__required-custom-fields--table-value">
        {valuesAsString(customFieldValues)}
      </td>
    </tr>
  );

  return (
    <>
      {controlledCustomFields && requiredCustomFieldKeys.length > 0 && (
        <div className="guest-upload__required-custom-fields">
          {!submissionAttempted && (
            <p>{bfTranslate('Please complete the following required fields before uploading assets to this')} {platformName}.</p>
          )}
          {submissionAttempted && !newAssetKeys && (
            <div className="guest-upload__required-custom-fields--reset">
              <p>{bfTranslate("See something that doesn't look right?")}</p>
              <TextButton
                onClick={(): void => {
                  setAllRequiredCustomFields(false);
                  setSubmissionAttempted(false);
                }}
              >
                {bfTranslate('Start over')}
              </TextButton>
            </div>
          )}
          {requiredCustomFieldKeys.length >= 0 && (
            <header className="custom-fields-list--heading">
              <div className="custom-fields-list--heading--key"><p>{bfTranslate('Key')}</p></div>
              <div className="custom-fields-list--heading--values"><p>{bfTranslate('Values')}</p></div>
            </header>
          )}
          <div className="guest-upload__required-custom-fields--list">
            {submissionAttempted ? (
              <table className="guest-upload__required-custom-fields--table">
                <tbody>
                  {Object.keys(customFieldsMap).slice(0).map((key) => (
                    renderCustomFields(customFieldsMap[key])
                  ))}
                </tbody>
              </table>
            ) : (
              <ul
                ref={listContainerRef}
                className={`custom-field-list bf-scroll-element ${containerHeight > 399 ? 'scrollable-list' : ''} ${requiredCustomFieldKeys.length >= 4 ? 'scrollable-list' : ''}`}
              >
                {requiredCustomFieldKeys && sortByPosition(requiredCustomFieldKeys)?.map(({ attributes, id }) => (
                  <CustomFieldKeyValueRow
                    key={id}
                    controlledCustomFieldsEnabled
                    customFieldKey={{
                      attributes,
                      id,
                      type: 'custom_field_keys'
                    }}
                    existingCustomFieldMap={customFieldsMap[id]}
                    handleCreate={genHandleCreate(id)}
                    handleDelete={genHandleDelete(id)}
                    handleRemoveValues={handleRemoveValues}
                    handleUpdate={genHandleUpdate(id)}
                    overflowParentRef={listContainerRef}
                    // Always say translating is false
                    // whatever locale they are adding in will
                    // create in the default locale as well
                    translating={false}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
};
